import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Plus, Trash2, Users, Mail, Phone, Pencil } from 'lucide-react';
import { toast } from 'sonner';

export const MisUsuariosPage = () => {
  const { user } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [selectedUsuario, setSelectedUsuario] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    password: ''
  });

  useEffect(() => {
    if (user?.cliente_id) {
      loadUsuarios();
    }
  }, [user]);

  const loadUsuarios = async () => {
    try {
      const response = await api.get(`/clientes/${user.cliente_id}/usuarios`);
      setUsuarios(response.data || []);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      email: '',
      telefono: '',
      password: ''
    });
    setIsEditing(false);
    setSelectedUsuario(null);
  };

  const handleOpenAdd = () => {
    resetForm();
    setShowForm(true);
  };

  const handleOpenEdit = (usuario) => {
    setFormData({
      nombre: usuario.nombre,
      email: usuario.email,
      telefono: usuario.telefono || '',
      password: ''
    });
    setSelectedUsuario(usuario);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!formData.nombre.trim() || !formData.email.trim()) {
      toast.error('Complete los campos obligatorios');
      return;
    }

    if (!isEditing && !formData.password) {
      toast.error('La contraseña es obligatoria para nuevos usuarios');
      return;
    }

    try {
      if (isEditing) {
        // Actualizar usuario
        const updateData = {
          nombre: formData.nombre,
          email: formData.email,
          telefono: formData.telefono
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await api.put(`/usuarios/${selectedUsuario.id}`, updateData);
        toast.success('Usuario actualizado');
      } else {
        // Crear nuevo usuario
        await api.post('/clientes/usuarios', {
          nombre: formData.nombre,
          email: formData.email,
          telefono: formData.telefono,
          password: formData.password,
          cliente_id: user.cliente_id,
          empresa_id: user.empresa_id
        });
        toast.success('Usuario creado');
      }
      setShowForm(false);
      resetForm();
      loadUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar usuario');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/usuarios/${selectedUsuario.id}`);
      toast.success('Usuario eliminado');
      setShowDelete(false);
      setSelectedUsuario(null);
      loadUsuarios();
    } catch (error) {
      toast.error('Error al eliminar usuario');
    }
  };

  if (!user?.cliente_id) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">No tiene un cliente asociado para gestionar usuarios.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mis-usuarios-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] text-gray-900">Mis Usuarios</h1>
          <p className="text-gray-600 mt-1">Gestione los usuarios de {user?.cliente_nombre}</p>
        </div>
        <Button onClick={handleOpenAdd} className="gap-2" data-testid="add-usuario-btn">
          <Plus size={18} />
          Agregar Usuario
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users size={20} />
            Usuarios del Cliente
          </CardTitle>
          <CardDescription>
            Estos usuarios pueden solicitar viajes en nombre de {user?.cliente_nombre}.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Cargando...</div>
          ) : usuarios.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No hay usuarios registrados. Agregue usuarios para que puedan solicitar viajes.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Teléfono</TableHead>
                  <TableHead className="w-32 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {usuarios.map((usuario) => (
                  <TableRow key={usuario.id}>
                    <TableCell className="font-medium">{usuario.nombre}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Mail size={14} className="text-gray-400" />
                        {usuario.email}
                      </div>
                    </TableCell>
                    <TableCell>
                      {usuario.telefono && (
                        <div className="flex items-center gap-2">
                          <Phone size={14} className="text-gray-400" />
                          {usuario.telefono}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenEdit(usuario)}
                          className="text-gray-500 hover:text-gray-700"
                          data-testid={`edit-usuario-${usuario.id}`}
                        >
                          <Pencil size={16} />
                        </Button>
                        {usuario.id !== user.id && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setSelectedUsuario(usuario);
                              setShowDelete(true);
                            }}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-usuario-${usuario.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog para agregar/editar */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Editar Usuario' : 'Agregar Usuario'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="nombre">Nombre Completo *</Label>
              <Input
                id="nombre"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                placeholder="Nombre del usuario"
                data-testid="usuario-nombre-input"
              />
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="correo@empresa.com"
                data-testid="usuario-email-input"
              />
            </div>
            <div>
              <Label htmlFor="telefono">Teléfono</Label>
              <Input
                id="telefono"
                value={formData.telefono}
                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                placeholder="+56 9 1234 5678"
                data-testid="usuario-telefono-input"
              />
            </div>
            <div>
              <Label htmlFor="password">
                {isEditing ? 'Nueva Contraseña (dejar vacío para mantener)' : 'Contraseña *'}
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={isEditing ? '••••••••' : 'Contraseña'}
                data-testid="usuario-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} data-testid="save-usuario-btn">
              {isEditing ? 'Guardar Cambios' : 'Crear Usuario'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AlertDialog para eliminar */}
      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar usuario?</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Está seguro que desea eliminar a "{selectedUsuario?.nombre}"? 
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
